/**
 * GLAMRDiP FAQ — search + category filter for the [data-gd-faq] section.
 *
 * Filters happen client-side: each question carries
 * `data-gd-faq-question-category="<id>"`; pills carry
 * `data-gd-faq-filter="<id>"` (or "all"). Search compares the question
 * text + answer text (lowercased substring) against the user input.
 *
 * Category headings (`<h2 data-gd-faq-category="<id>">`) auto-hide when
 * no question in their group is currently visible — keeps the layout
 * tidy on a partial filter. Empty-state paragraph
 * (`[data-gd-faq-empty]`) toggles when zero questions are visible.
 *
 * Defensive: try/catch around init so a single failure doesn't break
 * the page (especially the Shopify theme editor iframe).
 */
(() => {
  'use strict';

  class GDFAQ {
    constructor(root) {
      this.root = root;
      this.search = root.querySelector('[data-gd-faq-search]');
      this.filters = root.querySelectorAll('[data-gd-faq-filter]');
      this.items = root.querySelectorAll('[data-gd-faq-item]');
      this.categoryHeadings = root.querySelectorAll('[data-gd-faq-category]');
      this.empty = root.querySelector('[data-gd-faq-empty]');
      this.activeFilter = 'all';
      this.searchQuery = '';
      this._bind();
      this._apply();
    }

    _bind() {
      this.filters.forEach((btn) => {
        btn.addEventListener('click', () => {
          this.filters.forEach((b) => {
            b.classList.remove('is-active');
            b.setAttribute('aria-selected', 'false');
          });
          btn.classList.add('is-active');
          btn.setAttribute('aria-selected', 'true');
          this.activeFilter = btn.dataset.gdFaqFilter || 'all';
          this._apply();
        });
      });

      if (this.search) {
        let debounce = null;
        this.search.addEventListener('input', (e) => {
          clearTimeout(debounce);
          debounce = setTimeout(() => {
            this.searchQuery = (e.target.value || '').trim().toLowerCase();
            this._apply();
          }, 180);
        });
      }
    }

    _apply() {
      let visibleCount = 0;
      const visibleCats = new Set();
      this.items.forEach((item) => {
        const cat = item.dataset.gdFaqQuestionCategory || '';
        const qText = (item.querySelector('[data-gd-faq-question-text]')?.textContent || '').toLowerCase();
        const aText = (item.querySelector('[data-gd-faq-answer-text]')?.textContent || '').toLowerCase();
        const matchesCat = this.activeFilter === 'all' || cat === this.activeFilter;
        const matchesSearch = !this.searchQuery
          || qText.includes(this.searchQuery)
          || aText.includes(this.searchQuery);
        const visible = matchesCat && matchesSearch;
        item.hidden = !visible;
        if (visible) {
          visibleCount++;
          visibleCats.add(cat);
        }
      });

      // Hide category headings whose group has zero visible questions.
      this.categoryHeadings.forEach((h) => {
        const id = h.dataset.gdFaqCategory || '';
        h.hidden = !visibleCats.has(id);
      });

      if (this.empty) this.empty.hidden = visibleCount > 0;
    }
  }

  function init() {
    try {
      document.querySelectorAll('[data-gd-faq]').forEach((el) => {
        if (el.dataset.gdFaqInit === '1') return;
        el.dataset.gdFaqInit = '1';
        new GDFAQ(el);
      });
    } catch (e) {
      console.error('[gd-faq] init failed:', e);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
  document.addEventListener('shopify:section:load', () => init());
})();
